import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';

const TimelineCard = ({ company }) => {
    const timelineScreenRef = useRef(null);
    const cardRef = useRef(null);
    const linkRef = useRef(null);

    useEffect(() => {
        const timelineScreen = timelineScreenRef.current;
        const card = cardRef.current;
        const link = linkRef.current;

        if (timelineScreen && card && link) {
            // Animation functions (same for both hover and focus)
            const animateIn = () => {
                // Scale the timeline screen
                gsap.to(timelineScreen, {
                    duration: 0.5,
                    scaleX: 1.2,
                    scaleY: 1.2,
                    ease: "circ.out"
                });

                // Add box shadow to card
                gsap.to(card, {
                    boxShadow: "0 0 10px 5px #ffaa01",
                    duration: 0.5
                });
            };

            const animateOut = () => {
                // Reset scale
                gsap.to(timelineScreen, {
                    duration: 0.5,
                    scaleX: 1,
                    scaleY: 1,
                    ease: "circ.out"
                });

                // Remove box shadow
                gsap.to(card, {
                    boxShadow: "none",
                    duration: 0.5
                });
            };

            // Mouse event handlers
            const handleMouseEnter = animateIn;
            const handleMouseLeave = animateOut;

            // Focus event handlers
            const handleFocus = animateIn;
            const handleBlur = animateOut;

            // Add mouse event listeners to the entire card
            card.addEventListener('mouseenter', handleMouseEnter);
            card.addEventListener('mouseleave', handleMouseLeave);

            // Add focus event listeners to the link (focusable element)
            link.addEventListener('focus', handleFocus);
            link.addEventListener('blur', handleBlur);

            // Cleanup function
            return () => {
                card.removeEventListener('mouseenter', handleMouseEnter);
                card.removeEventListener('mouseleave', handleMouseLeave);
                link.removeEventListener('focus', handleFocus);
                link.removeEventListener('blur', handleBlur);
            };
        }
    }, []);

    return (
        <div className="col-12 col-lg-6 col-xl-4 col-xxl-3 pt-2">
            <div className="card h-100" ref={cardRef}>
                <div className="timeline-screen-frame card-img-top">
                    <div
                        className={`timeline-screen ${company.cssClass}`}
                        ref={timelineScreenRef}
                    >
                        <a
                            ref={linkRef}
                            href={company.link.href}
                            className="timeline-anchor"
                            title={company.link.title}
                            aria-label={company.link.ariaLabel}
                            aria-expanded="false"
                        >
                            <img
                                src={company.image.src}
                                className="timeline-img-placeholder img-fluid"
                                alt={company.image.alt}
                            />
                        </a>
                    </div>
                </div>
                <div className="card-body timeline-body">
                    <a
                        href={company.link.href}
                        title={company.link.title}
                        tabIndex={-1}
                    >
                        <h5 className="card-title">
                            {company.name}
                        </h5>
                        <p className="card-text">
                            {company.position} <em>({company.employmentType})</em>
                        </p>
                    </a>
                </div>
                <div className="card-footer">
                    <small className="text-body-secondary">
                        {company.startDate} &mdash; {company.endDate}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default TimelineCard;